import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/api";
import { RiskBadge } from "@/lib/risk";

const EMPTY = {
  name: "", grade: "Grade 10", course: "Mathematics", monthly_fee: 2500,
  joined: "June 2026", attendance_pct: 95, prev_attendance_pct: 95,
  latest_score: 80, prev_score: 80, homework_pct: 90, prev_homework_pct: 90,
  fee_status: "Paid", days_overdue: 0, parent_engagement: "High", last_contact_days: 3,
};

const NUM = (v) => (v === "" ? 0 : parseInt(v, 10) || 0);

export default function AddStudentDialog({ open, onOpenChange, onCreated, initial, isEdit }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Please enter the student's name"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthly_fee: NUM(form.monthly_fee),
        attendance_pct: NUM(form.attendance_pct),
        prev_attendance_pct: NUM(form.prev_attendance_pct),
        latest_score: NUM(form.latest_score),
        prev_score: NUM(form.prev_score),
        homework_pct: NUM(form.homework_pct),
        prev_homework_pct: NUM(form.prev_homework_pct),
        days_overdue: NUM(form.days_overdue),
        last_contact_days: NUM(form.last_contact_days),
      };
      const saved = isEdit
        ? await api.updateStudent(initial.id, payload)
        : await api.createStudent(payload);
      setResult(saved);
      toast.success(isEdit ? "Student updated — risk recalculated" : "Student added successfully");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setForm(initial || EMPTY); setResult(null); }, 200);
  };

  const Field = ({ label, k, type = "number", suffix }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-ink-muted">{label}</Label>
      <div className="relative">
        <Input
          data-testid={`field-${k}`}
          type={type}
          value={form[k]}
          onChange={(e) => set(k, type === "number" ? e.target.value : e.target.value)}
          className="h-10 rounded-lg"
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-muted">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? "Edit Student" : "Add Student"}
          </DialogTitle>
          <DialogDescription>
            Enter recent engagement data. Retention risk is calculated instantly from these signals.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-semibold text-ink-muted">Student Name</Label>
                <Input data-testid="field-name" value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Aarav Sharma" className="h-10 rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-ink-muted">Class</Label>
                <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
                  <SelectTrigger data-testid="field-grade" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12"].map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-ink-muted">Course</Label>
                <Select value={form.course} onValueChange={(v) => set("course", v)}>
                  <SelectTrigger data-testid="field-course" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Mathematics", "Science", "Physics", "Chemistry", "English"].map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Monthly Fee (₹)" k="monthly_fee" />
              <Field label="Attendance %" k="attendance_pct" suffix="%" />
              <Field label="Previous Attendance %" k="prev_attendance_pct" suffix="%" />
              <Field label="Latest Score %" k="latest_score" suffix="%" />
              <Field label="Previous Score %" k="prev_score" suffix="%" />
              <Field label="Homework Completion %" k="homework_pct" suffix="%" />
              <Field label="Previous Homework %" k="prev_homework_pct" suffix="%" />
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-ink-muted">Fee Status</Label>
                <Select value={form.fee_status} onValueChange={(v) => set("fee_status", v)}>
                  <SelectTrigger data-testid="field-fee_status" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paid">Paid</SelectItem>
                    <SelectItem value="Overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Days Overdue" k="days_overdue" />
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-ink-muted">Parent Engagement</Label>
                <Select value={form.parent_engagement} onValueChange={(v) => set("parent_engagement", v)}>
                  <SelectTrigger data-testid="field-parent_engagement" className="h-10 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Days Since Last Contact" k="last_contact_days" />
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4 text-center animate-rise">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-light">
              <span className="font-display text-2xl font-bold text-indigo-brand">{result.risk_score}</span>
            </div>
            <div>
              <div className="font-display text-xl font-bold text-ink">{result.name}</div>
              <div className="mt-2 flex justify-center"><RiskBadge level={result.risk_level} /></div>
            </div>
            <div className="mx-auto max-w-sm rounded-xl border border-border bg-secondary/50 p-4 text-left">
              <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Primary risk driver</div>
              <div className="mt-1 text-sm font-bold text-ink">{result.primary_signal}</div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={close} className="rounded-lg">Cancel</Button>
              <Button data-testid="save-student-btn" onClick={submit} disabled={saving}
                className="rounded-lg bg-indigo-brand hover:bg-indigo-deep">
                {saving ? "Calculating…" : isEdit ? "Save & Recalculate" : "Add & Calculate Risk"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={close} className="rounded-lg">Close</Button>
              <Button data-testid="view-profile-btn" onClick={() => { onCreated?.(result); close(); }}
                className="rounded-lg bg-indigo-brand hover:bg-indigo-deep">
                View Profile
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
